// src/app/pages/PaymentPage/index.tsx
import { useMemo } from "react";
import { ArrowLeft, Check, CreditCard, Shield, Sparkles } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { isCheckoutLocationState } from "@/app/core/TokensPayment/checkOutComp/subscribe/shared/checkoutTypes";
import { PaymentPageController } from "./controller/PaymentPageController ";
import { Button } from "@/app/core/ui/neitral.ui/button";
import { useTranslation } from "@/app/core/ui/theme/LanguageProvider";

export function PaymentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const controller = useMemo(() => new PaymentPageController(), []);
  const checkoutPayload = useMemo(
    () => (isCheckoutLocationState(location.state) ? location.state : null),
    [location.state],
  );

  return (
    <div className="h-full bg-transparent flex flex-col">
      {/* Стеклянный хедер */}
      <div className="glass mx-2 mt-2 rounded-2xl px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => navigate("/profile")}
          className="-ml-2 rounded-lg p-2 hover:bg-white/20 dark:hover:bg-white/10 transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="text-xl font-bold text-foreground">{t('pay')}</h1>
      </div>

      {/* Прозрачный контент */}
      <div className="flex-1 overflow-y-auto px-4 py-6">
        <div className="flex flex-col gap-4 max-w-md mx-auto w-full">
          {/* Детали заказа */}
          <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
            <h2 className="mb-4 text-base font-semibold text-foreground">{t('orderDetails')}</h2>
            <div className="mb-4 flex justify-between text-sm">
              <span className="text-muted-foreground">{t('tokenPackage')}</span>
              <span className="font-medium text-foreground">{checkoutPayload?.productName ?? "—"}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-base font-semibold text-foreground">{t('total')}</span>
              <span className="text-2xl font-bold text-foreground">
                {checkoutPayload ? controller.formatAmount(checkoutPayload.priceRub) : "—"}
              </span>
            </div>
          </div>

          {/* Состав пакета */}
          <div className="rounded-2xl border border-violet-200/20 bg-violet-500/10 p-5">
            <div className="mb-3 flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-violet-700" />
              <h2 className="text-base font-semibold text-foreground">{t('packageContents')}</h2>
            </div>
            <div className="flex flex-col gap-2">
              {(checkoutPayload?.features ?? []).slice(0, 5).map((feature) => (
                <div key={feature} className="flex items-start gap-2">
                  <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-violet-700" />
                  <span className="text-sm text-muted-foreground">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Способ оплаты */}
          <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
            <h2 className="mb-4 text-base font-semibold text-foreground">{t('paymentMethod')}</h2>
            <div className="flex items-center gap-3 rounded-xl bg-muted p-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 text-white">
                <CreditCard className="h-5 w-5" />
              </div>
              <div className="text-sm text-muted-foreground">{t('telegramPayments')}</div>
            </div>
          </div>

          {/* Безопасность */}
          <div className="flex items-start gap-3 rounded-2xl bg-muted/60 p-4">
            <Shield className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-600" />
            <div className="text-sm text-muted-foreground">{t('securePayment')}</div>
          </div>

          {/* Кнопка оплаты */}
          <Button
            variant="primary"
            size="lg"
            className="w-full bg-gradient-to-r from-violet-600 to-violet-700 hover:from-violet-700 hover:to-violet-800 text-white font-semibold shadow-lg"
            onClick={() => checkoutPayload && navigate("/profile/payment/validation", { state: checkoutPayload })}
            disabled={!checkoutPayload}
          >
            <CreditCard className="h-4 w-4 mr-2" />
            {checkoutPayload ? `${t('pay')} ${controller.formatAmount(checkoutPayload.priceRub)}` : t('pay')}
          </Button>
        </div>
      </div>
    </div>
  );
}